import { useCallback, useEffect, useRef, useState } from "react";
import { Title, useNotify, usePermissions } from "react-admin";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LinkIcon from "@mui/icons-material/Link";
import RefreshIcon from "@mui/icons-material/Refresh";

import { API_URL, createUploadSession, getImport, listImports } from "../../utils/ifpriApi";
import { buildUploadScript } from "./buildUploadScript";

const IFPRI_PAGE_URL = "https://sudan.ifpri.info/essential-commodities-prices-and-availability-ar/";
const POLL_MS = 3000;
const STALE_DAYS = 8;

const STEPS = {
  en: {
    dir: "ltr",
    title: "How to refresh the data (about 2 minutes)",
    steps: [
      'Click "Create upload link". A script appears below — click "Copy script". The link works once and expires after 30 minutes.',
      'Click "Open IFPRI page" and wait until the page has fully loaded (the charts are visible).',
      "On the IFPRI page press F12 (or right-click → Inspect) and open the Console tab.",
      'Paste the script and press Enter. If Chrome warns about pasting, type "allow pasting", press Enter, then paste again.',
      "Keep that tab open for about a minute while the console shows progress. When it says Done, come back here — the status below updates by itself.",
    ],
    note: "If the console shows an error, follow its instructions (usually: reload the IFPRI page and create a new link).",
  },
  ar: {
    dir: "rtl",
    title: "طريقة تحديث البيانات (حوالي دقيقتين)",
    steps: [
      'اضغط "Create upload link" وسيظهر نص برمجي بالأسفل، ثم اضغط "Copy script". الرابط يُستخدم مرة واحدة وتنتهي صلاحيته بعد 30 دقيقة.',
      'اضغط "Open IFPRI page" وانتظر حتى تكتمل الصفحة (تظهر الرسوم البيانية).',
      "في صفحة IFPRI اضغط F12 (أو زر الفأرة الأيمن ← Inspect) وافتح تبويب Console.",
      'الصق النص البرمجي واضغط Enter. إذا ظهر تحذير من Chrome بخصوص اللصق، اكتب "allow pasting" واضغط Enter ثم الصق مرة أخرى.',
      "اترك الصفحة مفتوحة لمدة دقيقة تقريبًا بينما يعرض الـConsole التقدّم. عند ظهور Done ارجع إلى هنا — الحالة بالأسفل تتحدّث تلقائيًا.",
    ],
    note: "إذا ظهر خطأ في الـConsole اتّبع التعليمات المكتوبة فيه (غالبًا: أعد تحميل صفحة IFPRI وأنشئ رابطًا جديدًا).",
  },
};

const STATUS_COLORS = { pending: "warning", success: "success", failed: "error" };
const StatusChip = ({ status }) => (
  <Chip label={status} color={STATUS_COLORS[status] || "default"} size="small" variant="outlined" />
);

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");
const daysSince = (d) => Math.floor((Date.now() - new Date(d).getTime()) / 86_400_000);

const IfpriDataPage = () => {
  const notify = useNotify();
  const { permissions } = usePermissions();
  const [lang, setLang] = useState("en");
  const [imports, setImports] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [creating, setCreating] = useState(false);
  const [session, setSession] = useState(null);
  const [script, setScript] = useState("");
  const [tracked, setTracked] = useState(null); // import being watched
  const scriptRef = useRef(null);

  const refreshList = useCallback(() => {
    setLoadingList(true);
    return listImports(20)
      .then((list) => {
        setImports(list);
        return list;
      })
      .catch((err) => notify(`Could not load imports: ${err.message}`, { type: "error" }))
      .finally(() => setLoadingList(false));
  }, [notify]);

  // On load, resume watching an upload link that is still open.
  useEffect(() => {
    refreshList().then((list) => {
      const pending = list?.find((i) => i.status === "pending");
      if (pending) setTracked(pending);
    });
  }, [refreshList]);

  // Poll the watched import until the browser upload arrives (or the link expires).
  useEffect(() => {
    if (!tracked || tracked.status !== "pending") return undefined;
    const timer = setInterval(async () => {
      try {
        const latest = await getImport(tracked.id);
        if (latest.status === "pending") return;
        setTracked(latest);
        refreshList();
        if (latest.status === "success") {
          notify("IFPRI data updated successfully.", { type: "success" });
          setSession(null);
          setScript("");
        } else {
          notify(`IFPRI import failed: ${latest.error}`, { type: "error" });
        }
      } catch (err) {
        // transient network error: keep polling
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [tracked, refreshList, notify]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const s = await createUploadSession();
      setSession(s);
      setScript(buildUploadScript(s, API_URL));
      setTracked({ id: s.importId, status: "pending", expires_at: s.expiresAt });
      refreshList();
    } catch (err) {
      notify(`Could not create upload link: ${err.message}`, { type: "error" });
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(script);
      notify("Script copied. Paste it in the Console on the IFPRI page.", { type: "info" });
    } catch (e) {
      // Clipboard API unavailable: select the text so the admin can press Ctrl+C.
      scriptRef.current?.select();
      notify("Press Ctrl+C to copy the selected script.", { type: "info" });
    }
  };

  if (permissions && permissions !== "admin") {
    return (
      <Card sx={{ m: 2, p: 2 }}>
        <Title title="IFPRI Prices" />
        <Alert severity="warning">Only admins can manage IFPRI data.</Alert>
      </Card>
    );
  }

  const lastSuccess = imports.find((i) => i.status === "success");
  const t = STEPS[lang];
  const watching = tracked?.status === "pending";

  return (
    <Card sx={{ m: 2, p: 2 }}>
      <Title title="IFPRI Prices" />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap">
        <Box>
          <Typography variant="h5">IFPRI Essential Commodities data</Typography>
          <Typography variant="body2" color="text.secondary">
            Shown on the website under Prices → Essential Commodities. Source: IFPRI Sudan (sudan.ifpri.info).
          </Typography>
        </Box>
        {lastSuccess ? (
          <Chip
            color={daysSince(lastSuccess.finished_at) >= STALE_DAYS ? "warning" : "success"}
            label={`Live data from ${fmtDate(lastSuccess.finished_at)} (${daysSince(lastSuccess.finished_at)} days ago)`}
          />
        ) : (
          !loadingList && <Chip color="warning" label="No data uploaded yet" />
        )}
      </Box>

      {/* Instructions */}
      <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6" dir={t.dir}>
            {t.title}
          </Typography>
          <ToggleButtonGroup size="small" exclusive value={lang} onChange={(_, v) => v && setLang(v)}>
            <ToggleButton value="en">English</ToggleButton>
            <ToggleButton value="ar">عربي</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box component="ol" dir={t.dir} sx={{ m: 0, pl: t.dir === "ltr" ? 3 : 0, pr: t.dir === "rtl" ? 3 : 0 }}>
          {t.steps.map((step) => (
            <Typography component="li" variant="body2" key={step} sx={{ mb: 0.75 }}>
              {step}
            </Typography>
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary" dir={t.dir} display="block" mt={1}>
          {t.note}
        </Typography>

        <Box display="flex" gap={1} mt={2} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
            onClick={handleCreate}
            disabled={creating}
          >
            {session ? "Create a new link" : "Create upload link"}
          </Button>
          <Button
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            href={session?.sourcePageUrl || IFPRI_PAGE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open IFPRI page
          </Button>
        </Box>

        {script && (
          <Box mt={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2">
                Upload script — valid until {fmtDate(session.expiresAt)}, works once
              </Typography>
              <Button size="small" variant="contained" startIcon={<ContentCopyIcon />} onClick={handleCopy}>
                Copy script
              </Button>
            </Box>
            <TextField
              inputRef={scriptRef}
              value={script}
              multiline
              fullWidth
              minRows={4}
              maxRows={8}
              InputProps={{ readOnly: true, sx: { fontFamily: "monospace", fontSize: 12 } }}
              onFocus={(e) => e.target.select()}
            />
          </Box>
        )}
      </Card>

      {/* Live status of the current upload */}
      {tracked && (
        <Alert
          severity={watching ? "info" : tracked.status === "success" ? "success" : "error"}
          icon={watching ? <CircularProgress size={20} /> : undefined}
          sx={{ mb: 2 }}
        >
          {watching && `Waiting for the upload from the IFPRI page (link #${tracked.id}, valid until ${fmtDate(tracked.expires_at)})…`}
          {tracked.status === "success" &&
            `Upload #${tracked.id} succeeded: ${tracked.response_count} datasets received and published.`}
          {tracked.status === "failed" && `Upload #${tracked.id} failed: ${tracked.error}`}
        </Alert>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* History */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h6">Recent uploads</Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={refreshList} disabled={loadingList}>
          Refresh
        </Button>
      </Box>
      {loadingList && !imports.length ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Finished</TableCell>
                <TableCell>Data range</TableCell>
                <TableCell>Datasets</TableCell>
                <TableCell>By</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {imports.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No uploads yet.
                  </TableCell>
                </TableRow>
              )}
              {imports.map((imp) => (
                <TableRow key={imp.id}>
                  <TableCell>{imp.id}</TableCell>
                  <TableCell>
                    <StatusChip status={imp.status} />
                  </TableCell>
                  <TableCell>{fmtDate(imp.started_at)}</TableCell>
                  <TableCell>{fmtDate(imp.finished_at)}</TableCell>
                  <TableCell>
                    {imp.date_from} → {imp.date_to}
                  </TableCell>
                  <TableCell>{imp.response_count || "—"}</TableCell>
                  <TableCell>{imp.triggered_by ?? "—"}</TableCell>
                  <TableCell sx={{ maxWidth: 420, whiteSpace: "normal", wordBreak: "break-word" }}>
                    {imp.error || ""}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Card>
  );
};

export default IfpriDataPage;
